import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { STORY_TYPES } from '../types/story';
import Logo from '../components/Logo';
import AudioPlayer from '../components/AudioPlayer';
import StoryGenerationProgress from '../components/StoryGenerationProgress';

interface StoryDetail {
  story_id: string;
  title: string;
  story_type: string;
  word_count: number;
  credits_used: number;
  status: string;
  created_at: string;
  completed_at?: string;
  generation_error?: string;
  progress?: {
    stage: string;
    progress: number;
    message: string;
  };
  audiobook?: {
    audiobook_id: string;
    duration_seconds: number;
    file_size_bytes: number;
    format: string;
    download_count: number;
  };
}

export default function StoryDetail() {
  const navigate = useNavigate();
  const { storyId } = useParams<{ storyId: string }>();
  const { user, clearAuth, accessToken } = useAuthStore();
  const [story, setStory] = useState<StoryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Poll for story status if generating
  useEffect(() => {
    if (!storyId) return;

    const fetchStory = async () => {
      try {
        const data = await api.getStory(storyId);
        setStory(data as StoryDetail);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching story:', err);
        setError('Failed to load story');
        setIsLoading(false);
      }
    };

    fetchStory();

    // Poll every 2 seconds if story is generating
    const interval = setInterval(() => {
      if (story?.status === 'generating') {
        fetchStory();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [storyId, story?.status]);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-text-medium">Loading story...</p>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="min-h-screen bg-background-cream">
        <header className="bg-white shadow-soft">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Logo size="sm" />
            <button onClick={() => navigate('/stories')} className="btn-outline px-4 py-2 text-sm">
              Back to Stories
            </button>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-3xl font-display font-bold text-text-dark mb-2">
            Story Not Found
          </h1>
          <p className="text-text-medium mb-6">{error || 'The story you are looking for does not exist.'}</p>
          <button onClick={() => navigate('/stories')} className="btn-primary">
            Go to My Stories
          </button>
        </div>
      </div>
    );
  }

  const storyType = STORY_TYPES.find((t) => t.value === story.story_type);
  const API_BASE_URL = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || 'https://storysprout-api.matthewspickford.workers.dev/api/v1';
  const audioUrl = story.audiobook && story.status === 'completed'
    ? `${API_BASE_URL}/audiobooks/${story.story_id}/download`
    : null;

  return (
    <div className="min-h-screen bg-background-cream">
      {/* Header */}
      <header className="bg-white shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo size="md" />
            <button
              onClick={() => navigate('/stories')}
              className="text-primary-600 hover:text-primary-700 font-semibold"
            >
              Stories
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-primary-600 hover:text-primary-700 font-semibold"
            >
              Dashboard
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-primary-500">Logged in as</p>
              <p className="text-emerald-600 font-semibold">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="btn-outline px-4 py-2 text-sm">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Story Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-soft p-6 sticky top-8">
              {/* Emoji & Type */}
              <div className="text-center mb-6">
                <div className="text-8xl mb-4">{storyType?.emoji}</div>
                <h3 className="text-sm text-purple-600 font-semibold uppercase tracking-wider mb-2">
                  {storyType?.label}
                </h3>
                <h1 className="text-2xl font-display font-bold text-green-600">
                  {story.title}
                </h1>
              </div>

              {/* Meta Info */}
              <div className="space-y-3 border-t border-gray-200 pt-6">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Word Count</span>
                  <span className="text-purple-600 font-semibold">{story.word_count.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Credits Used</span>
                  <span className="text-purple-600 font-semibold">{story.credits_used}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">Created</span>
                  <span className="text-purple-600 font-semibold">
                    {new Date(story.created_at).toLocaleDateString()}
                  </span>
                </div>
                {story.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-700 font-medium">Completed</span>
                    <span className="text-purple-600 font-semibold">
                      {new Date(story.completed_at).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {story.audiobook && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">Duration</span>
                      <span className="text-purple-600 font-semibold">
                        {Math.floor(story.audiobook.duration_seconds / 60)}m {story.audiobook.duration_seconds % 60}s
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700 font-medium">File Size</span>
                      <span className="text-purple-600 font-semibold">
                        {(story.audiobook.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Status Badge */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                {story.status === 'completed' && (
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <span className="text-green-700 font-semibold">✓ Ready to Listen</span>
                  </div>
                )}
                {story.status === 'generating' && (
                  <div className="text-center p-4 bg-yellow-50 rounded-xl">
                    <span className="text-yellow-700 font-semibold">Generating...</span>
                  </div>
                )}
                {story.status === 'failed' && (
                  <div className="text-center p-4 bg-red-50 rounded-xl">
                    <span className="text-red-700 font-semibold">✗ Generation Failed</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Audio Player / Progress */}
          <div className="lg:col-span-2">
            {story.status === 'completed' && audioUrl && accessToken ? (
              <AudioPlayer
                storyId={story.story_id}
                title={story.title}
                audioUrl={`${audioUrl}`}
              />
            ) : story.status === 'generating' && story.progress ? (
              <div className="bg-white rounded-2xl shadow-soft p-8">
                <h2 className="text-2xl font-display font-bold text-text-dark mb-6">
                  Creating Your Audiobook
                </h2>
                <StoryGenerationProgress
                  storyId={story.story_id}
                  onComplete={() => {}}
                  onError={() => {}}
                />
              </div>
            ) : story.status === 'failed' ? (
              <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
                <div className="text-6xl mb-4">😔</div>
                <h2 className="text-2xl font-display font-bold text-text-dark mb-2">
                  Generation Failed
                </h2>
                <p className="text-text-medium mb-6">
                  {story.generation_error || 'An error occurred while generating your audiobook.'}
                </p>
                <button className="btn-primary">
                  Try Again
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
                <div className="text-6xl mb-4">⏳</div>
                <h2 className="text-2xl font-display font-bold text-text-dark mb-2">
                  Audiobook Not Yet Ready
                </h2>
                <p className="text-text-medium">
                  Your audiobook is being prepared. Check back soon!
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
